import React from 'react';
import {assignProps} from '@sail/engine';
import type {PopoverProps} from '@sail/ui';
import {
  ButtonConfig,
  CompositeFieldConfig,
  DateFieldConfig,
  DialogConfig,
  DrawerConfig,
  PhoneNumberFieldConfig,
  SelectConfig,
  PopoverConfig,
  TooltipConfig,
  TextFieldConfig,
  css,
  tokens,
  useIsWithinListBoxContext,
  view,
} from '@sail/ui';
import {FormBlockConfig} from '@stripe-internal/ark-lib';
import {ARKAddressFieldConfig} from '@stripe-internal/ark';

import {useFlag} from '@sail/access';
import {BottomDialog} from '../../components/SharedComponents/Mobile/BottomDialog';
import {useShouldRenderPlaceholderBottomSheets} from './mobileUtils';
import {
  CONNECT_ELEMENT_IMPORTS,
  type ConnectElementImportKeys,
} from '../../connect/ConnectJSInterface/ConnectElementList';

const useMobilePopover = (props: PopoverProps) => {
  const [open, setOpen] = React.useState(true);

  // If the Popover is not entirely unmounted, we need to listen for it re-opening, like when rendering the `Menu` component
  React.useEffect(() => {
    if (props.open) {
      setOpen(true);
    }
  }, [props.open]);

  const {onClose, title} = props;

  const handleClose = React.useCallback(() => {
    setOpen(false);

    // Will properly close the hidden Popover
    onClose?.();
  }, [onClose]);

  // We don't override popovers when they are being used to display listboxes, like search results
  if (useIsWithinListBoxContext()) {
    return {};
  }

  return {
    style: {display: 'none'},
    children: (
      <>
        {open && (
          <BottomDialog handleClose={handleClose} title={title}>
            <view.div css={{width: 'fill'}}>{props.children}</view.div>
          </BottomDialog>
        )}
      </>
    ),
  };
};

const MobileUIOverrides = ({
  children,
  component,
  isMobileSdk,
}: {
  children: React.ReactNode;
  component: ConnectElementImportKeys;
  isMobileSdk: boolean;
}) => {
  const shouldRenderPlaceholderBottomSheets =
    useShouldRenderPlaceholderBottomSheets(component);
  const mobileUIEnabledForComponent =
    CONNECT_ELEMENT_IMPORTS[component]?.applyMobileUIOverrides;

  // Global flag for Sail bottom sheets, turns on Sail bottom sheets for ALL components, should be used for testing / bug bashing only
  const enableSailBottomSheetsGlobal = useFlag('enable_sail_bottom_sheets');

  // Flags which will be used to enable bottom sheets for specific components for rollouts
  const enableSailBottomSheetsPhaseOne =
    useFlag('sail_bottom_sheets_phase_one') && mobileUIEnabledForComponent;
  // Phase two rollout means ALL components will be using Sail bottom sheets, so no need to check the config
  // This flag is still check here to be extra explicit, and is used elsewhere to enable the filter chips
  const enableSailBottomSheetsPhaseTwo = useFlag(
    'sail_bottom_sheets_phase_two',
  );

  // todo(torrance, 2025-07-01): Remove this one we fully migrate to Sail bottom sheets
  const placeholderBottomSheetCSS = css({
    position: 'fixed',
    marginTop: 'calc(env(safe-area-inset-top, 0) + 40px)',
    maxHeight: 'calc(100% - calc(env(safe-area-inset-top, 0) + 40px))',
    height: 'auto',
    bottom: 'space.0',
    top: 'unset',

    borderTopLeftRadius: tokens.space[150],
    borderTopRightRadius: tokens.space[150],

    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  });

  const sharedMobileOverrides = [
    TooltipConfig.customize({uses: [assignProps({delay: 0})]}),
    ButtonConfig.customize({
      uses: [
        assignProps(isMobileSdk ? {size: 'large'} : {}),
        css({'-webkit-tap-highlight-color': 'transparent'}),
      ],
    }),
    ...(isMobileSdk
      ? [
          ARKAddressFieldConfig.customize({
            uses: [assignProps({gap: 'small', size: 'large'})],
          }),
          FormBlockConfig.customize({uses: [assignProps({gap: 'xlarge'})]}),
          TextFieldConfig.customize({uses: [assignProps({size: 'large'})]}),
          DateFieldConfig.customize({uses: [assignProps({size: 'large'})]}),
          SelectConfig.customize({uses: [assignProps({size: 'large'})]}),
          PhoneNumberFieldConfig.customize({
            uses: [assignProps({size: 'large'})],
          }),
          CompositeFieldConfig.customize({
            uses: [assignProps({size: 'large'})],
          }),
        ]
      : []),
  ];

  if (
    enableSailBottomSheetsGlobal ||
    enableSailBottomSheetsPhaseOne ||
    enableSailBottomSheetsPhaseTwo
  ) {
    return (
      <view.div
        uses={[
          ...sharedMobileOverrides,
          DialogConfig.customize({
            uses: [assignProps({preferredMobileDisplay: 'bottom-sheet'})],
          }),
          DrawerConfig.customize({
            uses: [assignProps({preferredMobileDisplay: 'bottom-sheet'})],
          }),
        ]}
      >
        {children}
      </view.div>
    );
  } else if (shouldRenderPlaceholderBottomSheets) {
    return (
      <view.div
        uses={[
          ...sharedMobileOverrides,
          PopoverConfig.customize({uses: [assignProps(useMobilePopover)]}),
          DialogConfig.customize({uses: [placeholderBottomSheetCSS]}),
          DrawerConfig.customize({uses: [placeholderBottomSheetCSS]}),
        ]}
      >
        {children}
      </view.div>
    );
  } else {
    return <>{children}</>;
  }
};

export default MobileUIOverrides;
